import { useCallback, useEffect } from 'react';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../api/push';
import { useCurrentUser } from './useCurrentUser';
import { isStandaloneDisplayMode } from '../utils/displayMode';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

/** PP15: does the browser subscription's own applicationServerKey still
 * match what the server is handing out today? Browsers that don't expose
 * `subscription.options` at all (older engines) can't be checked — treat
 * that as "assume compatible" rather than forcing a churn-y resubscribe on
 * every call for a platform this can't verify on. */
function vapidKeyMatches(existing: ArrayBuffer | null | undefined, desired: Uint8Array): boolean {
  if (!existing) return true;
  const existingBytes = new Uint8Array(existing);
  if (existingBytes.length !== desired.length) return false;
  for (let i = 0; i < desired.length; i++) {
    if (existingBytes[i] !== desired[i]) return false;
  }
  return true;
}

export type SubscribeOutcome = 'subscribed' | 'permission_denied' | 'unsupported' | 'not_configured';

/** P3-A12's browser-side half of the subscribe flow — the coach's "Allow"
 * step (via App.tsx's `onPermissionGranted`) and MySettings' urgent-buzz
 * toggle both call `subscribe()`. The SERVER remains the enforcement point
 * for the opt-out (`User.buzz_urgent_tasks` checked at send time, see
 * task_service._notify_urgent_assignment) — this hook additionally keeps
 * the BROWSER subscription itself in sync so there's an actual device to
 * send to when the toggle is on, and no dangling subscription lingering
 * after it's turned off.
 *
 * Every step is feature-detected — a platform missing `serviceWorker`,
 * `PushManager`, or `Notification` gets a clean 'unsupported' with no
 * console noise. A missing/unprovisioned VAPID key (empty string, a 404,
 * or any other network hiccup fetching it) resolves to 'not_configured'
 * just as quietly — this covers prod environments where the VAPID keypair
 * hasn't been generated yet (see push_service.py's module docstring). */
export function usePushSubscription() {
  const subscribe = useCallback(async (): Promise<SubscribeOutcome> => {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      typeof PushManager === 'undefined' ||
      typeof Notification === 'undefined'
    ) {
      return 'unsupported';
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'permission_denied';

    const vapid = await getVapidPublicKey().catch(() => null);
    if (!vapid || !vapid.configured || !vapid.public_key) return 'not_configured';

    const desiredKey = urlBase64ToUint8Array(vapid.public_key);
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (subscription && !vapidKeyMatches(subscription.options?.applicationServerKey, desiredKey)) {
      // PP15: the served VAPID key has rotated since this device last
      // subscribed — tear down the stale browser subscription so a fresh
      // one bound to the CURRENT key can replace it below.
      await subscription.unsubscribe().catch(() => {});
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // TS5's lib.dom types `Uint8Array` generically over `ArrayBufferLike`
        // (which includes `SharedArrayBuffer`), which no longer structurally
        // satisfies `BufferSource`'s `ArrayBufferView<ArrayBuffer>` — the
        // runtime value is a plain `Uint8Array` over a real `ArrayBuffer`
        // either way, so this cast is just working around the type-only gap.
        applicationServerKey: desiredKey as BufferSource,
      });
    }
    await subscribePush(subscription.toJSON() as PushSubscriptionJSON, navigator.userAgent);
    return 'subscribed';
  }, []);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await unsubscribePush(endpoint);
  }, []);

  return { subscribe, unsubscribe };
}

/** P3-A12 boot-time resubscribe/rotation path (PP15). Mounted once in
 * App.tsx, same pattern as `useAppBadge` — self-guards on `user` so it's
 * safe to mount unconditionally alongside the app's other always-on hooks.
 *
 * Handles the case a plain toggle-click can't: a RETURNING user who
 * already granted Notification permission and has the buzz toggle on, but
 * whose stored browser subscription has gone stale (e.g. ops rotated the
 * VAPID keypair, or the push service silently dropped the endpoint) — this
 * calls `subscribe()`, which detects and repairs that mismatch on its own
 * (see PP15 above), without waiting for the user to revisit MySettings.
 * Deliberately does NOT request permission itself — if permission isn't
 * already 'granted', there is nothing to fix here; the coach/settings
 * toggle are the only places that ever prompt. */
export function usePushBootResubscribe(): void {
  const { data: user } = useCurrentUser();
  const { subscribe } = usePushSubscription();

  useEffect(() => {
    if (!user || !user.buzz_urgent_tasks) return;
    if (!isStandaloneDisplayMode()) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    void subscribe().catch(() => {
      // Best-effort — a failure here just means the device stays on its
      // existing (possibly stale) subscription until the next boot or a
      // manual toggle; never worth surfacing to the user.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.buzz_urgent_tasks]);
}
